import type { RouteLocationNormalizedGeneric } from '#vue-router'

import { useSubscriptionsStore } from '@stores/subscriptions'

import {
  MAX_PER_PAGE,
  getFirstQueryValue,
  normalizeArrayQuery,
  toIdValue,
  toPaginationValue,
} from '@helpers/route'
import { parseArrayParam } from '@helpers/query-params'

import { defineNuxtRouteMiddleware, navigateTo } from '#app'

const STATUS_KEY = 'status'
const BRACKET_STATUS_KEY = /^status\[\d+]$/

/** `/subscriptions` — query → store filters, mirroring `middleware/orders.ts`. */
export default defineNuxtRouteMiddleware((to: RouteLocationNormalizedGeneric) => {
  // Links from app-main and legacy `/admin/subscriptions` bookmarks encode the array as `status[0]=…`;
  // rewrite it once to `status=…` so the filters only ever read one shape.
  const bracketStatuses = parseArrayParam(to.query, STATUS_KEY)

  if (bracketStatuses.length) {
    const query = Object.fromEntries(Object.entries(to.query).filter(([key]) => !BRACKET_STATUS_KEY.test(key)))

    return navigateTo({ path: to.path, query: { ...query, [STATUS_KEY]: bracketStatuses } }, { replace: true })
  }

  const store = useSubscriptionsStore()

  store.setFilterSearch(getFirstQueryValue(to.query?.q) || null)
  store.setFilterStatuses(normalizeArrayQuery(to.query?.status) ?? [])
  store.setFilterFrom(getFirstQueryValue(to.query?.from) || null)
  store.setFilterTo(getFirstQueryValue(to.query?.to) || null)
  // The customer page narrows the shared store to one customer; a visit here must not inherit it.
  store.setFilterCustomerId(toIdValue(to.query?.customer_id))

  store.setCurrentPage(toPaginationValue(to.query?.page, 1))
  store.setPerPage(toPaginationValue(to.query?.per_page, store.getPerPage, MAX_PER_PAGE))
})
